import { Icon } from "@atoms";
import { Hint } from "@atoms/index";
import { APIDeckTaxStrategies, BillingIntentionLabels, BillingIntentions } from "@constants/choices";
import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import Spinner from "@legacy/core/components/Spinner";
import TagField from "@legacy/core/fields/TagField";
import TagSearchInput from "@legacy/core/inputs/TagSearchInput";
import EquipmentSearchOrCreateSelect, { equipmentToEquipmentOption } from "@legacy/equipment/inputs/EquipmentSearchOrCreateSelect";
import { FileUpload } from "@molecules";
import { AccessCheck } from "@particles/index";
import { faIcon } from "@utils/faIcon";
import calculateProfit from "@utils/getObjectsProfit";
import { Component, Fragment } from "react";
import ClientSearchOrCreateSelect, { clientToClientOption } from "../../clients/inputs/ClientSearchOrCreateSelect";
import ServiceLocationSearchOrCreateSelect, { serviceLocationToServiceLocationOption } from "../../clients/inputs/ServiceLocationSearchOrCreateSelect";
import { renderClientString, renderServiceLocationString } from "../../clients/utils/utils";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import Banner from "../../core/components/Banner";
import LineItemListSelectFieldGroup from "../../core/components/LineItemListSelectFieldGroup";
import BasicDisplayField from "../../core/fields/BasicDisplayField";
import BasicSelectField from "../../core/fields/BasicSelectField";
import BooleanField from "../../core/fields/BooleanField";
import CharField from "../../core/fields/CharField";
import DateField from "../../core/fields/DateField";
import LinkedObjectsDisplayField from "../../core/fields/LinkedObjectsDisplayField";
import SearchCreateSelectField from "../../core/fields/SearchCreateSelectField";
import TextField from "../../core/fields/TextField";
import { valueIsDefined } from "../../core/utils/utils";
import JobObjectLink from "../../jobs/components/JobObjectLink";
import PriceBookServiceSearchOrCreateSelect, { priceBookServiceToPriceBookServiceOption } from "../../pricebook/inputs/PriceBookServiceSearchOrCreateSelect";
import PriceBookTaxSearchOrCreateSelect, { getPriceBookTaxObjectLabel, priceBookTaxToPriceBookTaxOption } from "../../pricebook/inputs/PriceBookTaxSearchOrCreateSelect";
import EstimateFeedAccordion from "../components/EstimateFeedAccordion";
import { calculateEstimateAmounts } from "../utils/utils";

class EstimateForm extends Component {

    constructor(props) {
        super(props)

        this.state = {
            clientSearchInput: "",
            billingClientSearchInput: "",
            serviceLocationSearchInput: "",
            serviceSearchInput: "",
            taxSearchInput: "",
            equipmentSearchInput: "",

            triggerCommit: false,
            triggerDiscard: false,
            pendingAction: null,
        }
    }

    handleSaveAction = (action) => {
        const { estimate, requestAction } = this.props

        if (estimate.id && window.FILE_UPLOADS_ENABLED) {
            this.setState({ triggerCommit: true, pendingAction: action })
        } else {
            requestAction(action)
        }
    }

    handleCancelAction = (action) => {
        const { estimate, requestAction } = this.props

        if (estimate.id && window.FILE_UPLOADS_ENABLED) {
            this.setState({ triggerDiscard: true, pendingAction: action })
        } else {
            requestAction(action)
        }
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    estimateIsSeen = (estimate) => {
        return valueIsDefined(estimate.id) && valueIsDefined(estimate.is_draft) && estimate.is_draft === false && estimate.last_viewed !== null
    }

    renderButtons = () => {
        const {
            mode,
            submitting,
            estimate,
            jobWalk,
            errors,
            onFormDataChange,
            requestAction,
            switchToSecondaryForm,
            updateClientSelection,
            updateBillingClientSelection,
            updateServiceLocationSelection,
            updatePriceBookServiceSelection,
            updatePriceBookTaxSelection,
            selectedClient,
            selectedBillingClient,
            selectedServiceLocation,
            selectedPriceBookService,
            selectedPriceBookTax,
            formatCurrencyValue,
            currencySymbol,
            showCustomEstimateIDField,
            foldDataComplete,
            showQuickBooksItemSelect=false,
            showTaxCreateButton=false,
            useTaxes=false,
            hideTaxRates=false,
            isCalculatingTax=false,
            updateAttachments,
            isUploading,
            returnScroll,
            defaultSelectedNet,
            netChoices
        } = this.props

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return (
                    <div className="data-panel__action-feedback">
                        <Spinner centered={true} />
                    </div>
                )
            }
            else {
                if (mode === "ADD_ESTIMATE") {
                    const showDraftButtonAtTheTop = window.CURRENT_USER?.permissions.estimates_edit_permission !== PERMISSION_LEVEL.FULL &&
                        window.CURRENT_USER?.permissions.estimates_create_permission === PERMISSION_LEVEL.RESTRICTED
                    return (
                        <ButtonGroup>
                            {
                                showDraftButtonAtTheTop && (
                                    <ButtonGroupRow>
                                        <UniversalButton type="primary" text="Save Draft" handler={event => this.handleSaveAction("ESTIMATE_CREATE_DRAFT")} disabled={isUploading || isCalculatingTax} />
                                    </ButtonGroupRow>
                                )
                            }
                            <AccessCheck permissions={{
                                estimates_create_permission: PERMISSION_LEVEL.FULL,
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text={
                                        window.CURRENT_USER?.permissions.estimates_edit_permission === PERMISSION_LEVEL.FULL ? "Create and Send" : "Create"
                                    } handler={event => this.handleSaveAction("ESTIMATE_CREATE")} disabled={isUploading || isCalculatingTax} />
                                </ButtonGroupRow>
                            </AccessCheck>
                            <ButtonGroupRow>
                                <AccessCheck permissions={{
                                    estimates_view_permission: PERMISSION_LEVEL.FULL,
                                }}>
                                    <UniversalButton type="secondary" text="Preview Estimate" handler={event => this.handleSaveAction("ESTIMATE_PREVIEW")} disabled={isUploading || isCalculatingTax} />
                                </AccessCheck>
                                {
                                    !showDraftButtonAtTheTop && (
                                        <AccessCheck permissions={{
                                            estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}>
                                            <UniversalButton type="secondary" text="Save Draft" handler={event => this.handleSaveAction("ESTIMATE_CREATE_DRAFT")} disabled={isUploading || isCalculatingTax} />
                                        </AccessCheck>
                                    )
                                }
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else if (mode === "EDIT_ESTIMATE") {
                    if (estimate.is_draft) {
                        return (
                            <ButtonGroup>
                                <AccessCheck permissions={{
                                    estimates_create_permission: PERMISSION_LEVEL.FULL,
                                }}>
                                    <ButtonGroupRow>
                                        <UniversalButton type="primary" text={
                                            window.CURRENT_USER?.permissions.estimates_edit_permission === PERMISSION_LEVEL.FULL ? "Create and Send" : "Create"
                                        } handler={event => this.handleSaveAction("ESTIMATE_CREATE")} disabled={isUploading || isCalculatingTax} />
                                    </ButtonGroupRow>
                                </AccessCheck>
                                <AccessCheck permissions={{
                                    estimates_view_permission: PERMISSION_LEVEL.FULL,
                                }}>
                                    <ButtonGroupRow>
                                        <UniversalButton type="secondary" text="Preview Estimate" handler={event => this.handleSaveAction("ESTIMATE_PREVIEW")} disabled={isUploading || isCalculatingTax} />
                                    </ButtonGroupRow>
                                </AccessCheck>
                                <ButtonGroupRow>
                                    <UniversalButton type="secondary" text="Save Draft" handler={event => this.handleSaveAction("ESTIMATE_CREATE_DRAFT")} disabled={isUploading || isCalculatingTax} />
                                    <AccessCheck permissions={{
                                        estimates_delete_permission: PERMISSION_LEVEL.RESTRICTED,
                                    }}>
                                        <UniversalButton type="danger" text="Delete Draft" handler={event => this.handleCancelAction("ESTIMATE_DELETE_DRAFT")} />
                                    </AccessCheck>
                                </ButtonGroupRow>
                            </ButtonGroup>
                        )
                    }
                    else {
                        return (
                            <ButtonGroup>
                                <AccessCheck permissions={{
                                    estimates_edit_permission: PERMISSION_LEVEL.FULL,
                                }}>
                                    {
                                        this.estimateIsSeen(estimate) && (
                                            <ButtonGroupRow>
                                                <UniversalButton type="primary" text="Save and Resend" handler={event => this.handleSaveAction("ESTIMATE_UPDATE_AND_RESEND")} disabled={isUploading || isCalculatingTax} />
                                            </ButtonGroupRow>
                                        )
                                    }
                                </AccessCheck>
                                <ButtonGroupRow>
                                    <AccessCheck permissions={{
                                        estimates_edit_permission: PERMISSION_LEVEL.FULL,
                                    }}>
                                        <UniversalButton type={this.estimateIsSeen(estimate) ? "secondary" : "primary"} text="Save" handler={event => this.handleSaveAction("ESTIMATE_UPDATE")} disabled={isUploading || isCalculatingTax} />
                                    </AccessCheck>
                                    <UniversalButton type="secondary" text="Cancel Edits" handler={event => this.handleCancelAction("ESTIMATE_CANCEL_EDITS")} />
                                </ButtonGroupRow>
                            </ButtonGroup>
                        )
                    }
                }
            }
        }
    }

    render() {
        const {
            mode,
            submitting,
            estimate,
            jobWalk,
            errors,
            onFormDataChange,
            requestAction,
            switchToSecondaryForm,
            updateClientSelection,
            updateBillingClientSelection,
            updateServiceLocationSelection,
            updatePriceBookServiceSelection,
            updatePriceBookTaxSelection,
            selectedClient,
            selectedBillingClient,
            selectedServiceLocation,
            selectedPriceBookService,
            selectedPriceBookTax,
            formatCurrencyValue,
            currencySymbol,
            showCustomEstimateIDField,
            foldDataComplete,
            showQuickBooksItemSelect=false,
            showTaxCreateButton=false,
            useTaxes=false,
            hideTaxRates=false,
            isCalculatingTax=false,
            defaultSelectedNet,
            netChoices,
            updateAttachments,
            removePendingAttachment,
            pendingSessionId,
            isUploading,
            setIsUploading,
            updateEquipmentSelection,
            selectedEquipment,
            showEquipmentWipedWarning,
            returnScroll
        } = this.props
        const {subtotal, tax, total, downPaymentAmountPaid, downPaymentAmountDue} = calculateEstimateAmounts(estimate)
        const { profit, profitBreakdown } = calculateProfit(estimate)

        const allowClientDetailsEdit = Object.keys(jobWalk).length === 0 && (estimate.job_walk === null || estimate.job_walk === undefined)

        return (
            <Fragment>
                <div id="estimate_form_react_wrapper">
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Estimate Create/Update">
                            <div className="data-panel__form" aria-label="Estimate Create/Update Form">
                                <p className="data-panel__form__caption">Please provide the following information to create your estimate:</p>
                                <fieldset>
                                    <legend>Client Details</legend>
                                    <SearchCreateSelectField
                                        fieldName="client"
                                        fieldLabel="Client"
                                        fieldValue={selectedClient !== null ? renderClientString(selectedClient) : null}
                                        inputComponent={
                                            <ClientSearchOrCreateSelect
                                                disabled={!allowClientDetailsEdit || window.CURRENT_USER?.permissions.clients_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                onSelectionChange={selectedOption => updateClientSelection(selectedOption != null ? selectedOption.object : null)}
                                                onInputChange={(input, action) => {action.action === "input-change" && this.setState({clientSearchInput: input})}}
                                                defaultSelected={selectedClient !== null ? clientToClientOption(selectedClient) : null}
                                                showCreateButton={window.CURRENT_USER?.permissions.clients_create_permission >= PERMISSION_LEVEL.FULL}
                                                onCreateClick={event => switchToSecondaryForm("ADD_CLIENT", null, {name: this.state.clientSearchInput})}
                                                listEndpointKwargs={[
                                                    ["is_active", true]
                                                ]}
                                            ></ClientSearchOrCreateSelect>
                                        }
                                        showButton={allowClientDetailsEdit && selectedClient !== null && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                                        buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Client</Fragment>}
                                        buttonAction={event => switchToSecondaryForm("EDIT_CLIENT", selectedClient, null)}
                                        disabled={!allowClientDetailsEdit}
                                        errors={errors}
                                    ></SearchCreateSelectField>
                                    {
                                        selectedClient !== null && selectedClient.notes !== "" && (
                                            <BasicDisplayField
                                                fieldName="client_notes"
                                                fieldLabel="Client Notes"
                                                fieldValue={selectedClient.notes}
                                                indented={true}
                                            ></BasicDisplayField>
                                        )
                                    }
                                    {
                                        selectedClient !== null && (
                                            <SearchCreateSelectField
                                                fieldName="service_location"
                                                fieldLabel="Service Location"
                                                fieldValue={selectedServiceLocation !== null ? renderServiceLocationString(selectedServiceLocation) : null}
                                                inputComponent={
                                                    <ServiceLocationSearchOrCreateSelect
                                                        disabled={!allowClientDetailsEdit || window.CURRENT_USER?.permissions.clients_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                        onSelectionChange={selectedOption => updateServiceLocationSelection(selectedOption != null ? selectedOption.object : null)}
                                                        onInputChange={(input, action) => {action.action === "input-change" && this.setState({serviceLocationSearchInput: input})}}
                                                        defaultSelected={selectedServiceLocation !== null ? serviceLocationToServiceLocationOption(selectedServiceLocation) : null}
                                                        client={selectedClient}
                                                        showCreateButton={window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                                                        onCreateClick={event => switchToSecondaryForm("ADD_SERVICE_LOCATION", null, {physical_address_street: this.state.serviceLocationSearchInput})}
                                                    ></ServiceLocationSearchOrCreateSelect>
                                                }
                                                showButton={allowClientDetailsEdit && selectedServiceLocation !== null  && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                                                buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Service Location</Fragment>}
                                                buttonAction={event => switchToSecondaryForm("EDIT_SERVICE_LOCATION", selectedServiceLocation, null)}
                                                disabled={!allowClientDetailsEdit}
                                                errors={errors}
                                            ></SearchCreateSelectField>
                                        )
                                    }
                                    {
                                        selectedClient !== null && selectedServiceLocation !== null && selectedServiceLocation.notes !== "" && (
                                            <BasicDisplayField
                                                fieldName="service_location_notes"
                                                fieldLabel="Service Location Notes"
                                                fieldValue={selectedServiceLocation.notes}
                                                indented={true}
                                            ></BasicDisplayField>
                                        )
                                    }
                                    {
                                        showEquipmentWipedWarning && (
                                            <Banner type="warning" text="Changing the client details has cleared your equipment selection." />
                                        )
                                    }
                                </fieldset>
                                {
                                    selectedClient !== null && selectedServiceLocation !== null && (
                                        <fieldset>
                                            <legend>Billing Details</legend>
                                            <BasicSelectField
                                                key={"billing_intention_" + (valueIsDefined(estimate.billing_intention) ? estimate.billing_intention : "default")}
                                                fieldName="billing_intention"
                                                fieldLabel="Billing Intention"
                                                fieldValue={valueIsDefined(estimate.billing_intention) ? estimate.billing_intention : BillingIntentions.SERVICE_LOCATION}
                                                fieldOnChange={billing_intention => {
                                                    const intentionValue = valueIsDefined(billing_intention) ? parseInt(billing_intention) : BillingIntentions.SERVICE_LOCATION;
                                                    onFormDataChange("billing_intention", intentionValue);

                                                    // Clear billing_client if not DIFFERENT_CLIENT
                                                    if (intentionValue !== BillingIntentions.DIFFERENT_CLIENT) {
                                                        updateBillingClientSelection(null);
                                                    }
                                                }}
                                                choices={[
                                                    { value: BillingIntentions.SERVICE_LOCATION, label: BillingIntentionLabels[BillingIntentions.SERVICE_LOCATION] },
                                                    { value: BillingIntentions.SERVICE_CLIENT, label: BillingIntentionLabels[BillingIntentions.SERVICE_CLIENT] },
                                                    { value: BillingIntentions.DIFFERENT_CLIENT, label: BillingIntentionLabels[BillingIntentions.DIFFERENT_CLIENT] },
                                                ]}
                                                errors={errors}
                                            ></BasicSelectField>
                                            {
                                                estimate.billing_intention === BillingIntentions.SERVICE_CLIENT && selectedClient && (
                                                    <Fragment>
                                                        <BasicDisplayField
                                                            fieldName="billing_to_client"
                                                            fieldLabel="Billing To"
                                                            fieldValue={renderClientString(selectedClient)}
                                                            indented={true}
                                                        ></BasicDisplayField>
                                                        {
                                                            !selectedClient.billing_address_formatted && (
                                                                <Banner
                                                                    type="warning"
                                                                    text="The selected service client has no billing address. Please add a billing address to it."
                                                                />
                                                            )
                                                        }
                                                    </Fragment>
                                                )
                                            }
                                            {
                                                estimate.billing_intention === BillingIntentions.DIFFERENT_CLIENT && (
                                                    <SearchCreateSelectField
                                                        key={"billing_client_" + (selectedBillingClient ? selectedBillingClient.id : "null")}
                                                        fieldName="billing_client"
                                                        fieldLabel="Billing Client"
                                                        fieldValue={selectedBillingClient ? renderClientString(selectedBillingClient) : null}
                                                        inputComponent={
                                                            <ClientSearchOrCreateSelect
                                                                disabled={window.CURRENT_USER?.permissions.clients_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                                onSelectionChange={selectedOption => updateBillingClientSelection(selectedOption != null ? selectedOption.object : null)}
                                                                onInputChange={(input, action) => {action.action === "input-change" && this.setState({billingClientSearchInput: input})}}
                                                                defaultSelected={selectedBillingClient ? clientToClientOption(selectedBillingClient) : null}
                                                                showCreateButton={window.CURRENT_USER?.permissions.clients_create_permission >= PERMISSION_LEVEL.FULL}
                                                                onCreateClick={event => switchToSecondaryForm("ADD_BILLING_CLIENT", null, {name: this.state.billingClientSearchInput || ""})}
                                                                listEndpointKwargs={[
                                                                    ["is_active", true],
                                                                    ["exclude_id", selectedClient?.id]
                                                                ]}
                                                            ></ClientSearchOrCreateSelect>
                                                        }
                                                        showButton={selectedBillingClient && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                                                        buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Client</Fragment>}
                                                        buttonAction={event => switchToSecondaryForm("EDIT_BILLING_CLIENT", selectedBillingClient, null)}
                                                        errors={errors}
                                                    ></SearchCreateSelectField>
                                                )
                                            }
                                            {
                                                selectedBillingClient !== null && selectedBillingClient.notes !== "" && window.CURRENT_USER?.permissions.clients_view_permission >= PERMISSION_LEVEL.FULL && (
                                                    <BasicDisplayField
                                                        fieldName="billing_client_notes"
                                                        fieldLabel="Billing Client Notes"
                                                        fieldValue={selectedBillingClient.notes}
                                                        indented={true}
                                                    ></BasicDisplayField>
                                                )
                                            }
                                            {
                                                estimate.billing_intention === BillingIntentions.DIFFERENT_CLIENT && selectedBillingClient && !selectedBillingClient.billing_address_formatted && (
                                                    <Banner
                                                        type="warning"
                                                        text="The selected billing client has no billing address. Please add a billing address to it."
                                                    />
                                                )
                                            }
                                        </fieldset>
                                    )
                                }
                                {
                                    selectedClient !== null && selectedServiceLocation !== null && (
                                        <fieldset>
                                            <legend>Basic Details</legend>
                                            <AccessCheck entitlements={["entitlement_jobs_enabled"]}>
                                                {
                                                    Object.keys(jobWalk).length !== 0 && !(estimate.job_walk === null || estimate.job_walk === undefined) && (
                                                        <LinkedObjectsDisplayField
                                                            fieldName="job_walk"
                                                            fieldLabel="Created From Job Walk"
                                                            objectComponentList={[
                                                                <JobObjectLink
                                                                    key="parent_job_walk_0"
                                                                    job={jobWalk}
                                                                    destination={DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, jobWalk.id)}
                                                                    disabled={window.CURRENT_USER?.permissions.jobs_view_permission < PERMISSION_LEVEL.FULL}
                                                                />
                                                            ]}
                                                        ></LinkedObjectsDisplayField>
                                                    )
                                                }
                                                {
                                                    Object.keys(jobWalk).length !== 0 && !(estimate.job_walk === null || estimate.job_walk === undefined) && jobWalk.origin_type !== 0 && (
                                                        <BasicDisplayField
                                                            fieldName="job_walk_origin"
                                                            fieldLabel={`${jobWalk.origin_type_label} ID`}
                                                            fieldValue={jobWalk.origin_id}
                                                        ></BasicDisplayField>
                                                    )
                                                }
                                                {
                                                    Object.keys(jobWalk).length !== 0 && !(estimate.job_walk === null || estimate.job_walk === undefined) && jobWalk.notes_to_dispatchers && window.CURRENT_USER?.permissions.jobs_view_permission >= PERMISSION_LEVEL.FULL && (
                                                        <BasicDisplayField
                                                            fieldName="notes_to_dispatchers"
                                                            fieldLabel="Notes From Technician"
                                                            fieldValue={jobWalk.notes_to_dispatchers}
                                                            indented={true}
                                                        ></BasicDisplayField>
                                                    )
                                                }
                                            </AccessCheck>
                                            {
                                                showCustomEstimateIDField
                                                ?
                                                <CharField
                                                    fieldName="custom_id"
                                                    fieldLabel="Estimate ID"
                                                    fieldValue={estimate.custom_id || ""}
                                                    fieldOnChange={custom_id => onFormDataChange("custom_id", custom_id || "")}
                                                    maxLength="15"
                                                    errors={errors}
                                                ></CharField>
                                                :
                                                <div id="div_id_custom_id">{errors.custom_id && <Banner type="warning" text={errors.custom_id} extraMargin={true} />}</div>
                                            }
                                            <SearchCreateSelectField
                                                fieldName="service_name"
                                                fieldLabel="Service Type"
                                                fieldValue={selectedPriceBookService ? selectedPriceBookService.description : (estimate.service_name || null)}
                                                inputComponent={
                                                    <PriceBookServiceSearchOrCreateSelect
                                                        disabled={!allowClientDetailsEdit || window.CURRENT_USER?.permissions.pricebook_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                        onSelectionChange={selectedOption => updatePriceBookServiceSelection(selectedOption != null ? selectedOption.object : null)}
                                                        onInputChange={(input, action) => {action.action === "input-change" && this.setState({serviceSearchInput: input})}}
                                                        defaultSelected={selectedPriceBookService ? priceBookServiceToPriceBookServiceOption(selectedPriceBookService) : (estimate.service_name ? {value: null, label: estimate.service_name} : null)}
                                                        showCreateButton={window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.FULL}
                                                        onCreateClick={event => switchToSecondaryForm("ADD_PRICEBOOKITEM_SERVICE", null, {description: this.state.serviceSearchInput})}
                                                    ></PriceBookServiceSearchOrCreateSelect>
                                                }
                                                showButton={allowClientDetailsEdit && selectedPriceBookService !== null && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                                buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Service Type</Fragment>}
                                                buttonAction={event => switchToSecondaryForm("EDIT_PRICEBOOKITEM_SERVICE", selectedPriceBookService, null)}
                                                disabled={!allowClientDetailsEdit}
                                                errors={errors}
                                            ></SearchCreateSelectField>
                                            <TextField
                                                fieldName="details"
                                                fieldLabel="Reason For Call"
                                                fieldValue={estimate.details || ""}
                                                fieldOnChange={details => onFormDataChange("details", details || "")}
                                                rows={3}
                                                placeholder="Customer states..."
                                                errors={errors}
                                            ></TextField>
                                            <SearchCreateSelectField
                                                fieldName="equipment"
                                                fieldLabel="Equipment"
                                                fieldValue={(selectedEquipment.length !== 0 ? selectedEquipment.map(equipment => equipment.display_name).join(", ") : "")}
                                                inputComponent={
                                                    <EquipmentSearchOrCreateSelect
                                                        disabled={window.CURRENT_USER?.permissions.equipment_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                        isMulti={true}
                                                        refetchSelection={false}
                                                        onSelectionChange={selectedOptions => updateEquipmentSelection((selectedOptions || []).map(option => option.object))}
                                                        onInputChange={(input, action) => {action.action === "input-change" && this.setState({equipmentSearchInput: input})}}
                                                        defaultSelected={selectedEquipment.map(equipment => equipmentToEquipmentOption(equipment))}
                                                        serviceLocation={selectedServiceLocation}
                                                        showCreateButton={window.CURRENT_USER?.permissions.equipment_create_permission >= PERMISSION_LEVEL.FULL}
                                                        onCreateClick={event => switchToSecondaryForm("ADD_EQUIPMENT", null, {display_name: this.state.equipmentSearchInput})}
                                                    ></EquipmentSearchOrCreateSelect>
                                                }
                                                showButton={window.CURRENT_USER?.permissions.equipment_list_permission >= PERMISSION_LEVEL.FULL}
                                                buttonLabel={<Fragment>View/Edit Logged Equipment{"  "}<Icon containerClassName="icon-inline" icon={faIcon({ name: "arrow-up-right-from-square", style: "regular" })} /></Fragment>}
                                                buttonAction={event => window.open(DjangoUrls["clients:clients-equipment"](window.MARKETPLACE_ENTITY_SLUG, selectedClient.id) + `?service_location_id=${selectedServiceLocation.id}`, "_blank")}
                                                errors={errors}
                                                optional={true}
                                            ></SearchCreateSelectField>
                                            <TagField
                                                fieldName="labels"
                                                fieldLabel="Labels"
                                                inputComponent={
                                                    <TagSearchInput
                                                        defaultSelectedTags={estimate.labels?.map(label => label.name) ?? []}
                                                        onSelectionChange={labelNames => onFormDataChange("labels", labelNames ? labelNames.map(labelName => ({"name": labelName})) : [])}
                                                        choicesEndpoint={DjangoUrls["estimates:api-estimates-labels-list"](window.MARKETPLACE_ENTITY_SLUG)}
                                                        placeholder="Add Labels..."
                                                        noOptionsMessage="No Labels Found"
                                                        isMulti={true}
                                                    ></TagSearchInput>
                                                }
                                                optional={true}
                                                errors={errors}
                                            ></TagField>
                                            <DateField
                                                fieldName="date_issued"
                                                fieldLabel="Issue Date"
                                                fieldValue={estimate.date_issued || ""}
                                                fieldOnChange={date_issued => onFormDataChange("date_issued", date_issued || "")}
                                                errors={errors}
                                            ></DateField>
                                            <BasicSelectField
                                                key={`net_${defaultSelectedNet}`}
                                                fieldName="net"
                                                fieldLabel="Valid For"
                                                fieldValue={valueIsDefined(defaultSelectedNet) ? defaultSelectedNet : ""}
                                                fieldOnChange={net => onFormDataChange("net", valueIsDefined(net) ? net : null)}
                                                choices={netChoices}
                                                errors={errors}
                                            ></BasicSelectField>
                                            <DateField
                                                key={`date_due_${estimate.date_due}`}
                                                fieldName="date_due"
                                                fieldLabel="Expiration Date"
                                                fieldValue={estimate.date_due || ""}
                                                fieldOnChange={date_due => onFormDataChange("date_due", date_due || "")}
                                                errors={errors}
                                            ></DateField>
                                            {
                                                window.ACCEPT_ONLINE_PAYMENTS && (
                                                    <BooleanField
                                                        fieldName="accept_online_payments"
                                                        fieldLabel="Payment Methods"
                                                        fieldValue={estimate.accept_online_payments}
                                                        fieldOnChange={accept_online_payments => onFormDataChange("accept_online_payments", accept_online_payments)}
                                                        inputLabel="Accept Online Card Payments"
                                                        errors={errors}
                                                    ></BooleanField>
                                                )
                                            }
                                        </fieldset>
                                    )
                                }
                                {
                                    selectedClient !== null && selectedServiceLocation !== null && foldDataComplete === true && (
                                        <fieldset>
                                            <legend>Charge Details</legend>
                                            <br />
                                            <AccessCheck permissions={{
                                                pricebook_list_permission: PERMISSION_LEVEL.RESTRICTED
                                            }}>
                                                <AccessCheck permissions={{
                                                    pricebook_view_permission: PERMISSION_LEVEL.FULL,
                                                }}>

                                                    <LineItemListSelectFieldGroup
                                                        object={estimate}
                                                        labelPrefix={"Estimated "}
                                                        formatCurrencyValue={formatCurrencyValue}
                                                        showQuickBooksItemSelect={showQuickBooksItemSelect}
                                                        useTaxes={useTaxes}
                                                        switchToSecondaryForm={switchToSecondaryForm}
                                                        errors={errors}
                                                        nonFieldErrorMessage={errors.line_items || "There's an issue with one or more of the items below. Try coming back to this page and trying again in a bit."}
                                                        isEstimateLineItem={true}
                                                    ></LineItemListSelectFieldGroup>
                                                </AccessCheck>
                                            </AccessCheck>
                                            {
                                                useTaxes && !hideTaxRates ? (
                                                    <SearchCreateSelectField
                                                        key={estimate.tax_name}
                                                        fieldName="tax_name"
                                                        fieldLabel="Tax Rate"
                                                        fieldValue={selectedPriceBookTax ? selectedPriceBookTax.description : (estimate.tax_name || null)}
                                                        inputComponent={
                                                            <PriceBookTaxSearchOrCreateSelect
                                                                disabled={window.CURRENT_USER?.permissions.pricebook_list_permission < PERMISSION_LEVEL.RESTRICTED}
                                                                onSelectionChange={selectedOption => updatePriceBookTaxSelection(selectedOption != null ? selectedOption.object : null)}
                                                                onInputChange={(input, action) => {action.action === "input-change" && this.setState({taxSearchInput: input})}}
                                                                defaultSelected={
                                                                    selectedPriceBookTax
                                                                    ?
                                                                    priceBookTaxToPriceBookTaxOption(selectedPriceBookTax)
                                                                    :
                                                                    (estimate.tax_name ? { value: null, label: getPriceBookTaxObjectLabel({ description: estimate.tax_name, default_price: estimate.tax_percent})} : null)
                                                                }
                                                                showCreateButton={showTaxCreateButton && window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.FULL}
                                                                limitToQuickBooksSyncedItems={!showTaxCreateButton}
                                                                onCreateClick={event => switchToSecondaryForm("ADD_PRICEBOOKITEM_TAX", null, {description: this.state.taxSearchInput})}
                                                            />
                                                        }
                                                        showButton={selectedPriceBookTax !== null && window.CURRENT_USER?.permissions.pricebook_create_permission >= PERMISSION_LEVEL.FULL}
                                                        buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Tax Rate</Fragment>}
                                                        buttonAction={event => switchToSecondaryForm("EDIT_PRICEBOOKITEM_TAX", selectedPriceBookTax, null)}
                                                        errors={errors}
                                                    />
                                                )
                                                :
                                                (
                                                    window.CURRENT_USER.service_company.apideck_tax_strategy === APIDeckTaxStrategies.automatic
                                                    ?
                                                    <>
                                                        <BasicDisplayField
                                                            fieldName="tax_name"
                                                            fieldLabel="Tax Rate"
                                                            fieldValue={isCalculatingTax ? "Calculating tax…" : (estimate.tax_name ? getPriceBookTaxObjectLabel({ description: estimate.tax_name, default_price: estimate.tax_percent}) : "")}
                                                        />
                                                        <Banner type="info" text="Sales tax is estimated and will be calculated at the time of invoicing." extraMargin={true} />
                                                    </>
                                                    :
                                                    <></>
                                                )
                                            }
                                            <TextField
                                                fieldName="comments"
                                                fieldLabel="Notes to Client"
                                                fieldValue={estimate.comments || ""}
                                                fieldOnChange={comments => onFormDataChange("comments", comments || "")}
                                                rows={3}
                                                placeholder="Add notes for your client on this Estimate."
                                                optional={true}
                                                errors={errors}
                                            ></TextField>
                                            <TextField
                                                fieldName="internal_notes"
                                                fieldLabel="Internal Notes"
                                                fieldValue={estimate.internal_notes || ""}
                                                fieldOnChange={internalNotes => onFormDataChange("internal_notes", internalNotes || "")}
                                                rows={3}
                                                placeholder="Add internal notes for your team on this Estimate."
                                                optional={true}
                                                errors={errors}
                                            ></TextField>
                                            {
                                                window.FILE_UPLOADS_ENABLED && estimate.id && (
                                                    <div className="data-panel__form__field">
                                                        <FileUpload
                                                            mode="attached"
                                                            objectType="estimate"
                                                            objectId={estimate.id}
                                                            existingAttachments={estimate.attachments}
                                                            autoUpload={true}
                                                            autoCommit={false}
                                                            triggerCommit={this.state.triggerCommit}
                                                            triggerDiscard={this.state.triggerDiscard}
                                                            label={{ label: "Attachments", isOptional: true }}
                                                            onCommitComplete={() => {
                                                                const action = this.state.pendingAction
                                                                this.setState({ triggerCommit: false, pendingAction: null })
                                                                if (action) {
                                                                    requestAction(action)
                                                                }
                                                            }}
                                                            onDiscardComplete={() => {
                                                                const action = this.state.pendingAction
                                                                this.setState({ triggerDiscard: false, pendingAction: null })
                                                                if (action) {
                                                                    requestAction(action)
                                                                }
                                                            }}
                                                            onUploadingChange={(uploading) => {
                                                                if (setIsUploading) {
                                                                    setIsUploading(uploading)
                                                                }
                                                            }}
                                                            onFileUploadError={(file, error) => {
                                                                console.error("Estimate attachment upload error:", file.name, error)
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                            {
                                                window.FILE_UPLOADS_ENABLED && !estimate.id && (
                                                    <div className="data-panel__form__field">
                                                        <FileUpload
                                                            mode="pending"
                                                            objectType="estimate"
                                                            sessionId={pendingSessionId}
                                                            autoUpload={true}
                                                            label={{ label: "Attachments", isOptional: true }}
                                                            onPendingFileUploadComplete={(result) => {
                                                                updateAttachments([{
                                                                    filePath: result.filePath,
                                                                    fileUrl: result.fileUrl,
                                                                    upload_filename: result.file.name,
                                                                    upload_mimetype: result.file.type,
                                                                    upload_size: result.file.size,
                                                                    sessionId: result.sessionId,
                                                                    isPending: true,
                                                                }])
                                                            }}
                                                            onFileRemove={(filename) => {
                                                                if (removePendingAttachment) {
                                                                    removePendingAttachment(filename)
                                                                }
                                                            }}
                                                            onUploadingChange={(uploading) => {
                                                                if (setIsUploading) {
                                                                    setIsUploading(uploading)
                                                                }
                                                            }}
                                                            onFileUploadError={(file, error) => {
                                                                console.error("Pending estimate attachment upload error:", file.name, error)
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                        </fieldset>
                                    )
                                }
                            </div>
                            {
                                selectedClient !== null && selectedServiceLocation !== null && foldDataComplete === true && (
                                    <div className="data-panel__form data-panel__data-summary amount-summary" aria-label="Amount Summary">
                                        <hr aria-hidden="true" />
                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--subtotal" aria-label="Estimated Subtotal">
                                            <div className="data-panel__data-summary__data__label"><span>Estimated Subtotal</span></div>
                                            <div className="data-panel__data-summary__data__value">{formatCurrencyValue(subtotal)}</div>
                                        </div>
                                        {
                                            useTaxes && (
                                                <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--tax" aria-label="Estimated Tax">
                                                    {
                                                        hideTaxRates
                                                        ?
                                                        <div className="data-panel__data-summary__data__label"><span title={estimate.tax_breakdown?.map(component => `[${component.rate}%] ${component.name}`).join("\n")}>{isCalculatingTax ? "Estimated Tax" : `Estimated Tax (${estimate.tax_percent ? `${estimate.tax_percent}%` : "none"})`}</span></div>
                                                        :
                                                        <div className="data-panel__data-summary__data__label"><span>Estimated Tax ({selectedPriceBookTax ? `${selectedPriceBookTax.default_price}%` : "none"})</span></div>
                                                    }
                                                    <div className="data-panel__data-summary__data__value">{isCalculatingTax ? "Calculating tax…" : formatCurrencyValue(tax)}</div>
                                                </div>
                                            )
                                        }
                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--down-payment">
                                            <div className="data-panel__data-summary__data__label">
                                                <div className="data-panel__form__field data-panel__form__field--inline" id="div_id_down_payment_amount" aria-label="Required Down Payment">
                                                    <label htmlFor="id_tax_down_payment" className="data-panel__form__field__label in-form">Required Down Payment <span className="text-optional">(optional)</span></label>
                                                    <div className="data-panel__form__field__input data-panel__form__field__input--tiny">
                                                        <div className="data-panel__form__field__input-group">
                                                            <div className="input-group-prepend">
                                                                <div className="input-group-text">{currencySymbol}</div>
                                                            </div>
                                                            <input type="number" min="0" step="0.5" name="down_payment_amount" defaultValue={estimate.down_payment_amount || 0} onChange={event => onFormDataChange(event.target.name, event.target.value)} id="id_down_payment_amount" autoComplete="off"></input>
                                                        </div>
                                                    </div>
                                                    {errors.down_payment_amount && <div className="data-panel__form__field__errors" aria-label="Field Errors">{errors.down_payment_amount}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <hr aria-hidden="true" />
                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--total" aria-label="Estimated Total">
                                            <div className="data-panel__data-summary__data__label"><span>Estimated Total</span></div>
                                            <div className="data-panel__data-summary__data__value">{isCalculatingTax ? "Calculating tax…" : formatCurrencyValue(total)}</div>
                                        </div>
                                                    <Hint
                                                        colorScheme={profit < 0 ? "tomato" : "blue"}
                                                        title="Estimated Profit Breakdown"
                                                        body={<>
                                                            <span style={{display:"flex", justifyContent: "space-between"}}>
                                                                Services
                                                                <span style={{paddingLeft:"1em"}}>{formatCurrencyValue(profitBreakdown.service)}</span>
                                                            </span>
                                                            <span style={{display:"flex", justifyContent: "space-between"}}>
                                                                Parts
                                                                <span style={{paddingLeft:"1em"}}>{formatCurrencyValue(profitBreakdown.part)}</span>
                                                            </span>
                                                            <span style={{display:"flex", justifyContent: "space-between"}}>
                                                                Misc.
                                                                <span style={{paddingLeft:"1em"}}>{formatCurrencyValue(profitBreakdown.other)}</span>
                                                            </span>
                                                            <span style={{display:"flex", justifyContent: "space-between"}}>
                                                                Discounts
                                                                <span style={{paddingLeft:"1em"}}>{formatCurrencyValue(profitBreakdown.discount)}</span>
                                                            </span>
                                                        </>}
                                                    >
                                                        <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--profit" aria-label="Estimated Profit">
                                                            <div className="data-panel__data-summary__data__label"><span className={profit<0 ? "text-alerting" : ""}>Estimated Profit</span></div>
                                                            <div className="data-panel__data-summary__data__value"><span className={profit<0 ? "text-alerting" : ""}>{formatCurrencyValue(profit)}</span></div>
                                                        </div>
                                                    </Hint>
                                        {
                                            this.estimateIsSeen(estimate) && (
                                                <Banner type="warning" text="The latest version of this estimate has been seen. We recommend resending it to your client with a message summarizing the revisions you've made." />
                                            )
                                        }
                                    </div>
                                )
                            }
                            {
                                selectedClient !== null && selectedServiceLocation !== null && foldDataComplete === true && this.renderButtons()
                            }
                        </div>
                    </div>
                </div>
                <AccessCheck permissions={{
                    estimates_view_permission: PERMISSION_LEVEL.FULL
                }}>
                    {
                        valueIsDefined(estimate.id) && (
                            <div className="accordion-wrapper">
                                <EstimateFeedAccordion estimateID={estimate.id} />
                            </div>
                        )
                    }
                </AccessCheck>
            </Fragment>
        )
    }

}

export default EstimateForm;
